/**
 * AccordionElement — F12.H T12.H.4.
 *
 * Akordeon — N sekcji rozwijanych, każda z tytułem. Można rozwijać
 * wiele naraz (allowMultiple) lub tylko jedną (default).
 *
 * **Uwaga arch.:** podobnie jak TabsElement — w T12.H.4 sekcje renderują
 * wspólne children (cała aktywna sekcja widzi wszystkie children).
 * Per-section children dorobimy w T12.H.10 z paletą buildera.
 *
 * Settings: { sections: [{ id, title, defaultExpanded? }], allowMultiple? }
 */

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

import type { LayoutElementDefinition } from '@/components/fields/LayoutElementRegistry';

interface AccordionSection {
  id: string;
  title: string;
  defaultExpanded?: boolean;
}

function AccordionView({
  sections,
  allowMultiple,
  children,
  isBuilder,
}: {
  sections: AccordionSection[];
  allowMultiple: boolean;
  children: React.ReactNode[];
  isBuilder: boolean;
}) {
  const initialExpanded = new Set(sections.filter((s) => s.defaultExpanded).map((s) => s.id));
  const [expanded, setExpanded] = useState<Set<string>>(initialExpanded);

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (!allowMultiple) next.clear();
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div
      className={`divide-y divide-border-primary rounded-lg border border-border-primary ${isBuilder ? 'ring-2 ring-transparent hover:ring-accent-200 transition' : ''}`}
    >
      {sections.length === 0 && isBuilder && (
        <p className="p-3 text-xs italic text-text-muted">
          (brak sekcji — dodaj w panelu właściwości)
        </p>
      )}
      {sections.map((section) => {
        const isOpen = expanded.has(section.id);
        return (
          <div key={section.id}>
            <button
              type="button"
              onClick={() => toggle(section.id)}
              aria-expanded={isOpen}
              className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium text-text-primary hover:bg-bg-secondary"
            >
              <span>{section.title}</span>
              <ChevronDown
                className={`h-4 w-4 text-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </button>
            {isOpen && (
              <div className="border-t border-border-primary p-3">
                {children.length > 0 ? (
                  <div className="space-y-3">{children}</div>
                ) : isBuilder ? (
                  <p className="text-xs italic text-text-muted">(zawartość sekcji pusta)</p>
                ) : null}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export const AccordionElement: LayoutElementDefinition = {
  type: 'accordion',
  category: 'interaktywne',
  isContainer: true,
  label: 'Akordeon',
  icon: 'ListCollapse',
  // F12.N E3.T1: accordionItems[] siedzi poza settings (na poziomie LayoutElement).
  // defaultSettings zwraca tylko `allowMultiple` — accordionItems dorzuca builder
  // page przy dodaniu akordeonu (1 panel `defaultExpanded=true`).
  defaultSettings: () => ({
    allowMultiple: false,
  }),
  render: ({ element, children, mode }) => {
    const settings = (element.settings ?? {}) as {
      sections?: AccordionSection[];
      allowMultiple?: boolean;
    };
    const sections = settings.sections ?? [];
    const isBuilder = mode === 'builder';

    return (
      <div data-testid={`accordion-element-${element.id}`} data-element-type="accordion">
        <AccordionView
          sections={sections}
          allowMultiple={settings.allowMultiple ?? false}
          children={children}
          isBuilder={isBuilder}
        />
      </div>
    );
  },
};
